/**
 * Capa de mapa de calor dibujada sobre un canvas.
 * Sustituye a google.maps.visualization.HeatmapLayer, retirada de la Maps
 * JavaScript API a partir de la versión 3.65.
 *
 * Expone la misma interfaz que se usaba de la capa original:
 * setMap(mapa), setData(puntos) y setOptions({ data, radius, opacity, gradient }).
 */

const GRADIENTE_POR_DEFECTO = [
  "rgba(0, 255, 0, 0)",
  "rgba(0, 255, 0, 1)",
  "rgba(85, 255, 0, 1)",
  "rgba(170, 255, 0, 1)",
  "rgba(255, 255, 0, 1)",
  "rgba(255, 213, 0, 1)",
  "rgba(255, 170, 0, 1)",
  "rgba(255, 128, 0, 1)",
  "rgba(255, 85, 0, 1)",
  "rgba(255, 43, 0, 1)",
  "rgba(255, 0, 0, 1)",
];

function construirPaleta(gradiente) {
  const lienzo = document.createElement("canvas");
  lienzo.width = 256;
  lienzo.height = 1;

  const contexto = lienzo.getContext("2d");
  const degradado = contexto.createLinearGradient(0, 0, 256, 0);
  const colores = gradiente.length > 1 ? gradiente : GRADIENTE_POR_DEFECTO;

  colores.forEach((color, indice) => {
    degradado.addColorStop(indice / (colores.length - 1), color);
  });

  contexto.fillStyle = degradado;
  contexto.fillRect(0, 0, 256, 1);

  return contexto.getImageData(0, 0, 256, 1).data;
}

function construirPincel(radio) {
  const lienzo = document.createElement("canvas");
  const tamano = radio * 2;
  lienzo.width = tamano;
  lienzo.height = tamano;

  const contexto = lienzo.getContext("2d");
  const degradado = contexto.createRadialGradient(
    radio,
    radio,
    0,
    radio,
    radio,
    radio,
  );
  degradado.addColorStop(0, "rgba(0, 0, 0, 1)");
  degradado.addColorStop(1, "rgba(0, 0, 0, 0)");

  contexto.fillStyle = degradado;
  contexto.fillRect(0, 0, tamano, tamano);

  return lienzo;
}

function coordenadaDe(punto, google) {
  if (!punto) return null;
  if (punto.location) return punto.location;
  if (punto instanceof google.maps.LatLng) return punto;
  if (punto.lat != null && punto.lng != null) {
    return new google.maps.LatLng(parseFloat(punto.lat), parseFloat(punto.lng));
  }
  return null;
}

let ClaseMapaCalor = null;

function definirClase(google) {
  if (ClaseMapaCalor) return ClaseMapaCalor;

  class MapaCalorOverlay extends google.maps.OverlayView {
    constructor(opciones = {}) {
      super();

      this.datos = opciones.data || [];
      this.radio = parseInt(opciones.radius, 10) || 25;
      this.opacidad =
        opciones.opacity != null ? parseFloat(opciones.opacity) : 0.6;
      this.gradiente = opciones.gradient || GRADIENTE_POR_DEFECTO;

      this.canvas = null;
      this.pincel = null;
      this.paleta = null;
      this.cuadroPendiente = null;

      if (opciones.map) this.setMap(opciones.map);
    }

    onAdd() {
      this.canvas = document.createElement("canvas");
      this.canvas.style.position = "absolute";
      this.canvas.style.pointerEvents = "none";
      this.canvas.style.opacity = this.opacidad;
      this.getPanes().overlayLayer.appendChild(this.canvas);
    }

    onRemove() {
      if (this.cuadroPendiente) {
        window.cancelAnimationFrame(this.cuadroPendiente);
        this.cuadroPendiente = null;
      }
      if (this.canvas && this.canvas.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
      this.canvas = null;
    }

    setData(datos) {
      this.datos = datos || [];
      this.draw();
    }

    setOptions(opciones = {}) {
      if (opciones.data !== undefined) this.datos = opciones.data || [];
      if (opciones.radius !== undefined) {
        this.radio = parseInt(opciones.radius, 10) || this.radio;
        this.pincel = null;
      }
      if (opciones.opacity !== undefined) {
        this.opacidad = parseFloat(opciones.opacity);
        if (this.canvas) this.canvas.style.opacity = this.opacidad;
      }
      if (opciones.gradient !== undefined) {
        this.gradiente = opciones.gradient || GRADIENTE_POR_DEFECTO;
        this.paleta = null;
      }
      if (opciones.map !== undefined) this.setMap(opciones.map);

      this.draw();
    }

    // Google llama a draw() en cada desplazamiento; el render se agrupa por cuadro
    draw() {
      if (!this.canvas || this.cuadroPendiente) return;

      this.cuadroPendiente = window.requestAnimationFrame(() => {
        this.cuadroPendiente = null;
        this.renderizar();
      });
    }

    renderizar() {
      const mapa = this.getMap();
      const proyeccion = this.getProjection();
      if (!this.canvas || !mapa || !proyeccion) return;

      const centro = mapa.getCenter();
      const contenedor = mapa.getDiv();
      if (!centro || !contenedor) return;

      const margen = this.radio * 2;
      const ancho = contenedor.offsetWidth + margen * 2;
      const alto = contenedor.offsetHeight + margen * 2;
      if (ancho <= 0 || alto <= 0) return;

      const centroPixel = proyeccion.fromLatLngToDivPixel(centro);
      const izquierda = centroPixel.x - ancho / 2;
      const arriba = centroPixel.y - alto / 2;

      this.canvas.width = ancho;
      this.canvas.height = alto;
      this.canvas.style.width = ancho + "px";
      this.canvas.style.height = alto + "px";
      this.canvas.style.left = izquierda + "px";
      this.canvas.style.top = arriba + "px";
      this.canvas.style.opacity = this.opacidad;

      const contexto = this.canvas.getContext("2d");
      contexto.clearRect(0, 0, ancho, alto);

      if (!this.datos.length) return;

      if (!this.pincel) this.pincel = construirPincel(this.radio);
      if (!this.paleta) this.paleta = construirPaleta(this.gradiente);

      const pesoMaximo = this.datos.reduce(
        (maximo, punto) => Math.max(maximo, parseFloat(punto.weight) || 1),
        1,
      );
      const anchoMundo = proyeccion.getWorldWidth();
      let dibujados = 0;

      this.datos.forEach((punto) => {
        const coordenada = coordenadaDe(punto, google);
        if (!coordenada) return;

        const pixel = proyeccion.fromLatLngToDivPixel(coordenada);
        const y = pixel.y - arriba;
        if (y < -this.radio || y > alto + this.radio) return;

        contexto.globalAlpha = Math.max(
          0.05,
          Math.min(1, (parseFloat(punto.weight) || 1) / pesoMaximo),
        );

        // El mundo se repite al alejar el zoom, así que se pintan las copias visibles
        [-anchoMundo, 0, anchoMundo].forEach((desplazamiento) => {
          const x = pixel.x + desplazamiento - izquierda;
          if (x < -this.radio || x > ancho + this.radio) return;
          contexto.drawImage(this.pincel, x - this.radio, y - this.radio);
          dibujados++;
        });
      });

      contexto.globalAlpha = 1;
      if (!dibujados) return;

      this.colorear(contexto, ancho, alto);
    }

    colorear(contexto, ancho, alto) {
      const imagen = contexto.getImageData(0, 0, ancho, alto);
      const pixeles = imagen.data;
      const paleta = this.paleta;

      for (let i = 3; i < pixeles.length; i += 4) {
        const intensidad = pixeles[i];
        if (!intensidad) continue;

        const indice = intensidad * 4;
        pixeles[i - 3] = paleta[indice];
        pixeles[i - 2] = paleta[indice + 1];
        pixeles[i - 1] = paleta[indice + 2];
        pixeles[i] = (intensidad * paleta[indice + 3]) / 255;
      }

      contexto.putImageData(imagen, 0, 0);
    }
  }

  ClaseMapaCalor = MapaCalorOverlay;
  return ClaseMapaCalor;
}

/**
 * @param {object} google Referencia a window.google ya cargada
 * @param {{ data?: Array, map?: object, radius?: number, opacity?: number, gradient?: Array }} opciones
 */
export function crearMapaCalor(google, opciones = {}) {
  const Clase = definirClase(google);
  return new Clase(opciones);
}

export default crearMapaCalor;
